<template>
  <div class="flex flex-col gap-8 items-start">
    <h3 v-if="isFreeTier" class="h-[12px]" />
    <h3 class="font-bold">
      {{ headerText }}
    </h3>
    <div v-for="item in featureItems" :key="`${item.text}-${item.injection}`">
      <div class="flex items-center text-base">
        <component :is="isLockedItem(item) ? BittsTooltip : 'span'">
          <template #title>
            <p>Locked platform feature</p>
          </template>
          <FontAwesomeIcon
            :icon="iconForItem(item)"
            class="h-12 w-12 mr-8 text-neutral-accent"
          />
        </component>
        <component :is="item.tooltip ? BittsTooltip : 'span'">
          <template #title>
            <p>
              {{ item.tooltip }}
            </p>
          </template>
          <p class="text-neutral-text inline-flex items-center gap-4">
            <span
              v-html="
                item.tooltip
                  ? injectStrings(item.text, {
                      values: [item.injection],
                      bold: false,
                      italic: false,
                      underline: true,
                    })
                  : item.text
              "
            />
            <BittsTag
              v-if="item.isBeta && !(item.isMcpServer && hasMcpMeteringUi)"
              color="beta"
              size="x-small"
              text="Beta"
            />
          </p>
        </component>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { BittsTag, BittsTooltip } from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { BILLING_IMPROVEMENTS_COPY } from '@/constants/billing';
import type { BillingFeature } from '@/constants/billing';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { useBillingStore, useFeatureFlagStore } from '@/stores';
import { injectStrings } from '@/utils';

interface Props {
  tier: string;
}

const props = defineProps<Props>();

const { hasFeatureFlag } = useFeatureFlagStore();
const { isLegacyConnectorPPv5 } = storeToRefs(useBillingStore());
const hasMcpMeteringUi = computed(() =>
  hasFeatureFlag(FEATURE_FLAG.TEMP_MCP_METERING_UI),
);

const headerText = computed(() => {
  if (isFreeTier.value) return;
  let prevTier;
  switch (props.tier) {
    case 'connector':
      prevTier = 'Free';
      break;
    case 'supernode':
      prevTier = 'Connector';
      break;
    default:
      prevTier = 'Supernode';
  }
  return `Everything in ${prevTier}, plus:`;
});

const isFreeTier = computed(() => props.tier === 'explorer');

const featureItems = computed(() => {
  return BILLING_IMPROVEMENTS_COPY[props.tier]?.features_v5?.map((item) => ({
    ...item,
    tooltip:
      hasMcpMeteringUi.value && item.tooltipCreditSuffix && item.tooltip
        ? `${item.tooltip}${item.tooltipCreditSuffix}`
        : item.tooltip,
  }));
});

function iconForItem(item: BillingFeature) {
  if (item.isPlatformFeature && isLegacyConnectorPPv5.value)
    return ['fak', 'lock'];
  return ['far', 'check'];
}

function isLockedItem(item: BillingFeature) {
  return !!item.isPlatformFeature && isLegacyConnectorPPv5.value;
}
</script>
